import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { ErrorMessage } from "@traqly/ui/components/error-message";
import { Label } from "@traqly/ui/components/label";
import { cn } from "@traqly/ui/lib/utils";

const inputVariants = cva(
  "flex w-full bg-card text-mono-900 placeholder:text-mono-400 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 transition-all",
  {
    variants: {
      variant: {
        default:
          "border border-mono-300 hover:border-gray-600 hover:bg-input-bg-hover focus-visible:border-primary-700 focus-visible:ring-2 focus-visible:ring-primary-500/50",
        short:
          "w-full max-w-[406px] border border-mono-300 hover:border-gray-600 hover:bg-input-bg-hover focus-visible:border-primary-700 focus-visible:ring-2 focus-visible:ring-primary-500/50",
        narrow:
          "w-full max-w-[220px] border border-mono-300 hover:border-gray-600 hover:bg-input-bg-hover focus-visible:border-primary-700 focus-visible:ring-2 focus-visible:ring-primary-500/50",
        error:
          "border-2 border-error-700 focus-visible:border-input-border-error",
        "error-short":
          "border-2 max-w-[406px] border-error-700 focus-visible:border-input-border-error",
      },
      inputSize: {
        sm: cn(
          "h-8 px-2 py-2 text-xs rounded-lg gap-1.5 [&_svg]:size-4",
          "text-body-small-regular!",
          "[>]",
        ),
        md: cn(
          "h-10 px-2.5 py-2.5 text-sm rounded-[0.625rem] gap-2 [&_svg]:size-5",
          "text-body-medium-regular!",
        ),
        lg: cn(
          "h-14 px-4 py-4 text-base rounded-xl gap-3 [&_svg]:size-6",
          "text-body-large-regular!",
        ),
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "md",
    },
  },
);

export interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  label?: string;
  description?: string;
  error?: string;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  optional?: boolean;
  labelSize?: string;
  // Keeps the error border variant without rendering the inline
  // ErrorMessage below the input — for a caller that lays this Input out
  // next to siblings (e.g. a unit Select) in a row: the error message
  // growing just this child's height fights a shared `items-end`/flex
  // alignment, and the caller renders the message itself, outside the row.
  hideErrorMessage?: boolean;
}

function Input({
  className,
  type,
  variant,
  inputSize,
  label,
  description,
  error,
  leadingIcon,
  trailingIcon,
  optional,
  labelSize,
  id,
  disabled,
  hideErrorMessage,
  ...props
}: InputProps) {
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const inputVariant =
    error && variant === "short" ? "error-short" : error ? "error" : variant;
  return (
    <div className="space-y-2">
      {label && (
        <Label
          className={labelSize}
          htmlFor={inputId}
          optional={optional}
        >
          {label}
        </Label>
      )}
      <div className="relative">
        {leadingIcon && (
          <div className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-gray-600">
            {leadingIcon}
          </div>
        )}
        <input
          id={inputId}
          type={type}
          disabled={disabled}
          className={cn(
            inputVariants({ variant: inputVariant, inputSize }),
            leadingIcon && {
              "pl-10": inputSize === "sm",
              "pl-11": inputSize === "md",
              "pl-14": inputSize === "lg",
            },
            trailingIcon && {
              "pr-10": inputSize === "sm",
              "pr-11": inputSize === "md",
              "pr-14": inputSize === "lg",
            },
            disabled && "bg-mono-200",
            className,
          )}
          {...props}
        />
        {trailingIcon && (
          <div className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-600">
            {trailingIcon}
          </div>
        )}
      </div>
      {error && !hideErrorMessage && <ErrorMessage text={error} />}
      {description && (
        <p className="text-input-description text-sm">{description}</p>
      )}
    </div>
  );
}

Input.displayName = "Input";

export { Input, inputVariants };
